import type { ReactNode } from 'react';
import { Card, CardHeader, CardTitle, CardBody, Chip, Badge } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Boxes } from 'lucide-react';
import { formatMoney, formatDate } from '@/lib/format';
import {
  useServiceConfigoptions,
  useServiceAddonConfigoptions,
  type ServiceConfigOption,
  type ServiceRow,
  type ServiceDetail,
} from '@/api/services';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

/** One CDN table row: a bandwidth configoption of the service, or an addon service. */
interface CdnRow {
  key: string;
  entityName?: string | null;
  bandwidth: string;
  isAddon?: boolean;
  commitment_value?: number;
  commitment_time?: number;
  commitment_prepaid?: boolean | null;
  commitment_price_per_unit?: number;
  commitment_price_total?: number;
  service_price_recurring?: number;
  employee_names?: string[];
  service_start_date?: string | null;
}

const muted = { color: 'var(--vn-fg-muted)' } as const;
const nf = new Intl.NumberFormat('en-US');
const rightHeader = (label: string) => () => <span style={{ display: 'block', textAlign: 'right' }}>{label}</span>;

/** Bandwidth label like legacy (local/global BW), otherwise the raw metric name. */
function bandwidthLabel(name: string, single: boolean): string {
  if (single) return 'Total bandwidth';
  const map: Record<string, string> = { local: 'Local BW', global: 'Global BW' };
  return map[name] ?? name;
}

/** Committed value: PAYG when unset, else "1,000 GB" (+ "/ Nmo" when a multi-month term). */
function commitmentCell(row: CdnRow): ReactNode {
  const v = row.commitment_value;
  if (!v) return <span style={{ ...muted, fontSize: 'var(--vn-font-size-xs)' }}>PAYG</span>;
  const suffix = row.commitment_time && row.commitment_time > 1 ? `GB / ${row.commitment_time}mo` : 'GB';
  return <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{`${nf.format(v)} ${suffix}`}</span>;
}

function buildRows(detail: ServiceDetail, base: ServiceConfigOption[], addons: (ServiceRow & { configoptions?: ServiceConfigOption[] })[]): CdnRow[] {
  const single = base.length === 1;
  const rows: CdnRow[] = base.map((c) => ({
    key: `base-${c.service_configoption_id}`,
    entityName: detail.name,
    bandwidth: bandwidthLabel(c.name, single),
    commitment_value: c.commitment_value,
    commitment_time: c.commitment_time,
    commitment_prepaid: c.commitment_prepaid,
    commitment_price_per_unit: c.commitment_price_per_unit,
    commitment_price_total: c.commitment_price_total,
    service_price_recurring: detail.service_price_recurring,
    employee_names: detail.employee_names,
    service_start_date: detail.service_start_date,
  }));
  for (const a of addons) {
    rows.push({
      key: `addon-${a.service_id}`,
      entityName: a.name,
      bandwidth: `[ADDON] - ${a.service_name}`,
      isAddon: true,
      commitment_value: a.configoptions?.[0]?.commitment_value ?? 0,
      commitment_time: 1,
      commitment_price_total: a.service_price_register,
      service_price_recurring: a.service_price_recurring,
      employee_names: detail.employee_names,
      service_start_date: a.service_start_date,
    });
  }
  return rows;
}

/**
 * CDN Information config table — one row per bandwidth commitment (local/global/…)
 * plus each addon service, mirroring legacy Conversant/CDN. Read-only; editing
 * lives in the Configuration tab.
 */
export function CdnConfigTable({ detail }: { detail: ServiceDetail }) {
  const id = detail.service_id;
  const currency = detail.service_currency || 'VND';
  const base = useServiceConfigoptions(id);
  const addons = useServiceAddonConfigoptions(id);

  if (base.isError) return <TabError title="Failed to load configuration" onRetry={() => base.refetch()} />;
  if (base.isLoading) return <TabLoading rows={4} />;

  const money = (v?: number) => <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{v ? formatMoney(v, currency) : '—'}</span>;

  const baseRows = base.data?.results ?? [];
  const rows = buildRows(detail, baseRows, addons.data?.results ?? []);
  const hasPrice = baseRows.some((c) => (c.commitment_value ?? 0) > 0);

  const columns: ColumnDef<CdnRow, unknown>[] = [
    { id: 'entity', header: 'Portal/Domain/IP', cell: ({ row }) => row.original.entityName || <span style={muted}>—</span> },
    {
      id: 'bandwidth',
      header: 'Bandwidth',
      cell: ({ row }) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.bandwidth}</span>
          {row.original.isAddon ? <Badge tone="info" appearance="subtle" size="sm">addon</Badge> : null}
        </span>
      ),
    },
    { id: 'commitment_value', header: rightHeader('Committed'), cell: ({ row }) => commitmentCell(row.original) },
  ];
  if (hasPrice) {
    columns.push({
      id: 'commitment_prepaid',
      header: 'Payment',
      cell: ({ row }) =>
        (row.original.commitment_value ?? 0) > 0 ? (
          <Chip appearance="subtle" tone={row.original.commitment_prepaid ? 'success' : 'neutral'} size="sm">
            {row.original.commitment_prepaid ? 'Prepaid' : 'Postpaid'}
          </Chip>
        ) : null,
    });
  }
  columns.push({ id: 'unit_price', header: rightHeader('Unit price'), cell: ({ row }) => money(row.original.commitment_price_per_unit) });
  if (hasPrice) {
    columns.push({ id: 'total_price', header: rightHeader('Total price'), cell: ({ row }) => money(row.original.commitment_price_total) });
    columns.push({ id: 'recurring', header: rightHeader('Recurring'), cell: ({ row }) => money(row.original.service_price_recurring) });
  }
  columns.push({ id: 'sales', header: 'Sales', cell: ({ row }) => row.original.employee_names?.filter(Boolean).join(', ') || <span style={muted}>—</span> });
  if (hasPrice) {
    columns.push({ id: 'term', header: rightHeader('Term'), cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{row.original.commitment_time || '—'}</span> });
  }
  columns.push({ id: 'start', header: rightHeader('Start date'), cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{formatDate(row.original.service_start_date)}</span> });

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Configuration</CardTitle>
      </CardHeader>
      <CardBody>
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => r.key}
          emptyState={<EmptyState icon={<Boxes size={24} />} title="Pay as you go" description="No committed bandwidth — billed on usage." />}
        />
      </CardBody>
    </Card>
  );
}
